import React, { useMemo } from 'react';
import classNames from 'classnames';
import PencilIcon from 'icons/Pencil.icon';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from 'ncoded-component-library';
import DragScroll from 'components/DragScroll';
import ResolveImage from 'components/ResolveImage';
import PreviewGalleryModal from '../PreviewGalleryModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { PreviewGalleryModalRef } from '../PreviewGalleryModal/PreviewGalleryModal.component';
import { useNavigate } from 'react-router-dom';
import { DropzoneFile } from 'components/Dropzone/utils';
import { getImgUrl } from 'components/ResolveImage/utils';
import { getSellingPortfolio } from 'utils';

import './GalleryContainerCard.styles.scss';

type GalleryContainerCardProps = {
  className?: string;
  gallery: DropzoneFile[];
  isLoading?: boolean;
  dealRoomId: string;
  asBuyer?: boolean;
  isDisabled?: boolean;
};

const GalleryContainerCard: React.FC<GalleryContainerCardProps> = (props) => {
  const { className, gallery, isLoading, dealRoomId, asBuyer, isDisabled } =
    props;
  const [modal, modalRef] = useCallbackRef<PreviewGalleryModalRef>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const classes = classNames('gallery-container-card', className);

  const renderImages = useMemo(
    () =>
      !isLoading ? (
        gallery.map(({ key, url, name }, index) => (
          <ResolveImage
            key={key}
            src={getImgUrl(url, '480')}
            alt={name}
            rootProps={{
              onClick: () => {
                modal?.setSelectedIndex(index);
                modal?.open();
              },
            }}
          />
        ))
      ) : (
        <Loader />
      ),
    [gallery, isLoading, modal],
  );

  const emptyState = useMemo(
    () => (
      <div className="gallery-container-card__empty-state">
        <p>{t('noPhotosYet')}</p>
        <p>
          {asBuyer ? t('galleryEmptyMessageBuyer') : t('galleryEmptyMessage')}
        </p>
      </div>
    ),
    [asBuyer, t],
  );

  const renderContent = useMemo(
    () => (gallery.length || isLoading ? renderImages : emptyState),
    [emptyState, gallery.length, isLoading, renderImages],
  );

  return (
    <div className={classes}>
      <div className="gallery-container-card__heading">
        <p>{t('gallery')}</p>
        {!asBuyer && (
          <Button
            variant="icon"
            aria-label="Gallery"
            disabled={isDisabled}
            icon={PencilIcon}
            onClick={() => {
              if (isDisabled) return;
              navigate(getSellingPortfolio(dealRoomId, 'gallery'));
            }}
          />
        )}
      </div>
      <DragScroll
        adjustToMobile
        wrapperClassname="gallery-container-card__images"
      >
        {renderContent}
      </DragScroll>
      <PreviewGalleryModal gallery={gallery} ref={modalRef} />
    </div>
  );
};

export default GalleryContainerCard;
